import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import {removeAllEndParentheses} from "../../utils/utils";

/**
 * @param children An array of JSX elements.
 * @param initialHeight The initial height of the list
 * @param fadeHeight The height of the fade-out. Default 30
 * @param mutable set the div for show more to not rerender when the children change
 * @param fadeColor is the bg color you want to fade to/from
 * @returns {Element} Displays the array as a list that fades out to a show more
 * @constructor
 */
const FadingList = ({ children, initialHeight = 0, fadeHeight = 30, mutable = true, fadeColor = "rgba(255, 255, 255)"}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isExpandable, setIsExpandable] = useState(false);
    const contentRef = useRef(null);
    const controls = useAnimation();
    const [childKey, setChildKey] = useState(0);

    const showMoreBuffer = 0;

    const insertColor = removeAllEndParentheses(fadeColor)

    const toggleOpen = () => {
        setIsOpen(prev => !prev);
    };

    useEffect(() => {
        if (mutable) {
            setChildKey(prevKey => prevKey + 1);
        }
        setIsOpen(false);
        setIsExpandable(false);
    }, [children, mutable]);

    useEffect(() => {
        if (contentRef.current) {
            const newHeight = contentRef.current.scrollHeight;
            if (newHeight > initialHeight + showMoreBuffer) {
                setIsExpandable(true);
            } else {
                setIsExpandable(false);
            }

            controls.start({
                height: isOpen ? newHeight : isExpandable ? initialHeight : "auto",
                transition: { duration: 0.3 }
            });
        }
    }, [children, isOpen, initialHeight, controls, isExpandable]);

    useEffect(() => {
        if (contentRef.current) {
            controls.start({
                height: isOpen ? contentRef.current.scrollHeight + 10 : isExpandable ? initialHeight : "auto",
                transition: { duration: 0.3 }
            });
        }
    }, [initialHeight, isOpen, controls, isExpandable]);

    return (
        <AnimatePresence initial={false}>
            <div style={{ position: 'relative',}}>
                <motion.div
                    key={childKey}
                    ref={contentRef}
                    animate={controls}
                    initial={{height: isOpen ? contentRef.current.scrollHeight + 10 : isExpandable ? initialHeight : "auto"}}
                    style={{ overflow: 'hidden' }}
                >
                    <div>
                        {children}
                        {!isOpen && isExpandable && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 29,
                                    left: 0,
                                    right: 0,
                                    height: fadeHeight,
                                    background: `linear-gradient(to bottom, ${insertColor}, 0), ${insertColor}, 1))`,
                                    pointerEvents: 'none'
                                }}
                            />
                        )}
                    </div>
                </motion.div>
                {isExpandable && (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button onClick={toggleOpen} style={{ color: "#5388d8", borderStyle: "none", background: "none", height: 30 }}>
                            {isOpen ? 'Show Less' : 'Show More'}
                        </button>
                    </div>
                )}
            </div>
        </AnimatePresence>
    );
};

export default FadingList;
